/**
 * Pipelines Section — settings section for managing deal pipelines and the
 * contact lifecycle.
 *
 * Surfaces two sub-sections:
 * - Pipelines: list with create / rename / edit-stages / delete actions.
 * - Contact Lifecycle: a single "Edit stages" button that opens the
 *   stage-only flavor of `StagesSlideout`. The lifecycle has no pipeline
 *   object — there's exactly one stage taxonomy per org.
 */

import { useQueryClient } from '@tanstack/react-query';
import { Plus, Settings01, Star01, Trash01, UserUp01 } from '@untitledui/icons';
import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { useAuthInfo } from '@/allauth_auth/hooks.js';
import { useOrg } from '@/contexts/OrgContext';
import { usePermissions } from '@/hooks/usePermissions';
import { Badge } from '@/lib/base/badges/badges';
import { Button } from '@/lib/base/buttons/button';
import { ConfirmationDialog } from '@/lib/base/overlays';
import { Table, TableCard } from '@/lib/base/table';
import { Tooltip, TooltipTrigger } from '@/lib/base/tooltip/tooltip';
import { createCRMObjectClient } from '@/lib/crm/core/CRMObjectClient';
import { getUserFriendlyEventError } from '@/lib/crm/core/errors';
import { createEventBuilder } from '@/lib/crm/core/EventBuilder';
import { createEventsClient } from '@/lib/crm/core/EventsClient';
import { fetchFreshSeq } from '@/lib/crm/core/utils';
import { useObjectTypes } from '@/lib/crm/hooks/useObjectTypes';
import type { Pipeline } from '@/lib/crm/hooks/usePipelines';
import { usePipelines } from '@/lib/crm/hooks/usePipelines';
import { CreatePipelineSlideout } from '@/lib/crm/list/components/CreatePipelineSlideout';
import type { StagesSlideoutConfig } from '@/lib/crm/list/components/StagesSlideout';
import {
  lifecycleStagesConfig,
  StagesSlideout,
} from '@/lib/crm/list/components/StagesSlideout';
import { getEntityTypeLabel } from '@/lib/crm/utils/entityTypeUtils';
import { logger } from '@/lib/logger';
import { useViewHealthMarker } from '@/lib/view-health';

// ============================================================================
// Helpers
// ============================================================================

/**
 * Build the relative path for a pipeline's board view.
 *
 * Deal pipelines use the dedicated `/pipeline/deals/:id` route; other entity
 * types use the generic `/pipeline/:entitySlug/:id` route where the slug is
 * the base entity type name with underscores replaced by hyphens (matching
 * how `GenericCrmList` resolves slugs via `toSnakeCase`).
 */
function getPipelinePath(
  orgId: number,
  pipeline: Pipeline
): string | undefined {
  if (!pipeline.entity_id) {
    return undefined;
  }
  const baseType =
    pipeline.base_entity_type_name ||
    pipeline.entity_type_name.replace('_pipeline', '');
  const slug = baseType === 'ahoy_deal' ? 'deals' : baseType.replace(/_/g, '-');
  return `/${orgId}/pipeline/${slug}/${pipeline.entity_id}`;
}

// ============================================================================
// PipelineRow
// ============================================================================

interface PipelineRowProps {
  pipeline: Pipeline;
  orgId: number;
  onEditStages: (p: Pipeline) => void;
  onSetDefault: (p: Pipeline) => void;
  onDelete: (p: Pipeline) => void;
}

function PipelineRow({
  pipeline,
  orgId,
  onEditStages,
  onSetDefault,
  onDelete,
}: PipelineRowProps) {
  const { hasPermission } = usePermissions();

  const canEditStages = hasPermission(
    pipeline.entity_type_name,
    'ahoy_property_updated'
  );
  const canDelete = hasPermission(
    pipeline.entity_type_name,
    'ahoy_object_deleted'
  );

  const handleEditStages = useCallback(
    () => onEditStages(pipeline),
    [onEditStages, pipeline]
  );
  const handleSetDefault = useCallback(
    () => onSetDefault(pipeline),
    [onSetDefault, pipeline]
  );
  const handleDelete = useCallback(
    () => onDelete(pipeline),
    [onDelete, pipeline]
  );

  const isUneditable = pipeline.entity_id === null;
  const isDefaultPipeline = pipeline.is_default;
  const canActuallyDelete = canDelete && !isDefaultPipeline;
  // "Set as default" is a property write (ahoy_is_default), so it's gated on the
  // same update permission as renaming/stage edits.
  const canSetDefault = canEditStages && !isDefaultPipeline && !isUneditable;

  let deleteTooltip = 'Delete pipeline';
  if (isUneditable) {
    deleteTooltip = 'This pipeline cannot be edited';
  } else if (isDefaultPipeline) {
    deleteTooltip = 'Default pipeline cannot be deleted';
  }

  const rowId =
    pipeline.entity_id ??
    `${pipeline.entity_type_name}:${pipeline.pipeline_name}`;

  const pipelinePath = getPipelinePath(orgId, pipeline);

  return (
    <Table.Row id={rowId}>
      <Table.Cell>
        <div className='flex items-center gap-2'>
          {pipelinePath ? (
            <Link
              to={pipelinePath}
              className='text-sm font-medium text-fg-primary hover:text-brand-primary hover:underline transition-colors'
            >
              {pipeline.label}
            </Link>
          ) : (
            <span className='text-sm font-medium text-fg-primary'>
              {pipeline.label}
            </span>
          )}
          {isDefaultPipeline && (
            <Badge type='pill-color' size='sm' color='brand'>
              Default
            </Badge>
          )}
        </div>
      </Table.Cell>
      <Table.Cell>
        <span className='text-sm text-fg-tertiary'>
          {pipeline.stages.length}{' '}
          {pipeline.stages.length === 1 ? 'stage' : 'stages'}
        </span>
      </Table.Cell>
      <Table.Cell>
        <div className='flex items-center gap-2 justify-end'>
          {/* Set as default button — hidden for the current default */}
          {canSetDefault && (
            <Tooltip title='Set as default pipeline'>
              <TooltipTrigger
                aria-label='Set as default pipeline'
                className='p-1.5 rounded-md text-fg-quaternary hover:text-fg-primary hover:bg-bg-tertiary transition-colors cursor-pointer focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2'
                onPress={handleSetDefault}
              >
                <Star01 className='h-4 w-4' />
              </TooltipTrigger>
            </Tooltip>
          )}

          {/* Edit pipeline button */}
          <Tooltip
            title={
              isUneditable ? 'This pipeline cannot be edited' : 'Edit pipeline'
            }
          >
            <TooltipTrigger
              aria-label='Edit pipeline'
              className='p-1.5 rounded-md text-fg-quaternary hover:text-fg-primary hover:bg-bg-tertiary transition-colors cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2'
              onPress={handleEditStages}
              isDisabled={!canEditStages || isUneditable}
            >
              <Settings01 className='h-4 w-4' />
            </TooltipTrigger>
          </Tooltip>

          {/* Delete button */}
          <Tooltip title={deleteTooltip}>
            <TooltipTrigger
              aria-label={deleteTooltip}
              className='p-1.5 rounded-md text-fg-quaternary hover:text-error-primary hover:bg-error-secondary transition-colors cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2'
              onPress={handleDelete}
              isDisabled={!canActuallyDelete || isUneditable}
            >
              <Trash01 className='h-4 w-4' />
            </TooltipTrigger>
          </Tooltip>
        </div>
      </Table.Cell>
    </Table.Row>
  );
}

// ============================================================================
// PipelineGroup
// ============================================================================

/**
 * Notes → Tasks retirement: note pipelines are never offered as a fresh,
 * empty section (no schema-driven "create" path), and an org's pre-existing
 * note pipelines only surface while the viewer still has Note read access.
 */
const NOTE_PIPELINE_ENTITY_TYPE_NAME = 'ahoy_note_pipeline';
const NOTE_BASE_ENTITY_TYPE_NAME = 'ahoy_note';

interface PipelineGroup {
  /** Pipeline object entity type, e.g. `ahoy_deal_pipeline`. */
  pipelineEntityTypeName: string;
  /** Base entity the pipeline organizes, e.g. `ahoy_deal`. */
  baseEntityTypeName: string;
  /** Human-readable singular label for the base entity, e.g. "Deal". */
  label: string;
  /** Whether a USER principal may create this pipeline type (schema user_creatable; default true when not derivable). */
  userCreatable: boolean;
  pipelines: Pipeline[];
}

/**
 * Resolve the base entity type for a pipeline. Prefers the explicit
 * `base_entity_type_name` from the API, falling back to stripping the
 * `_pipeline` suffix (matching the derivation used for the stages slideout).
 */
function getBaseEntityType(pipeline: Pipeline): string {
  return (
    pipeline.base_entity_type_name ||
    pipeline.entity_type_name.replace('_pipeline', '')
  );
}

interface PipelineGroupSectionProps {
  group: PipelineGroup;
  orgId: number;
  onCreate: (group: PipelineGroup) => void;
  onEditStages: (p: Pipeline) => void;
  onSetDefault: (p: Pipeline) => void;
  onDelete: (p: Pipeline) => void;
}

/**
 * A single entity-type group: a heading, a type-scoped create button, and the
 * table of pipelines for that type (e.g. all Deal pipelines). Card layout
 * configuration is available in the pipeline edit slideout's "Card Layout" tab.
 */
function PipelineGroupSection({
  group,
  orgId,
  onCreate,
  onEditStages,
  onSetDefault,
  onDelete,
}: PipelineGroupSectionProps) {
  const { hasPermission } = usePermissions();
  const canCreate =
    group.userCreatable &&
    hasPermission(group.pipelineEntityTypeName, 'ahoy_object_created');

  const handleCreate = useCallback(() => onCreate(group), [onCreate, group]);

  return (
    <div className='space-y-3'>
      <div className='flex items-center justify-between gap-3'>
        <h3 className='text-sm font-semibold text-fg-primary'>
          {`${group.label} pipelines`}
        </h3>
        {canCreate && (
          <Button
            size='sm'
            color='secondary'
            iconLeading={Plus}
            onClick={handleCreate}
          >
            {`Create ${group.label.toLowerCase()} pipeline`}
          </Button>
        )}
      </div>
      {group.pipelines.length === 0 ? (
        <div className='rounded-lg border border-border-secondary p-6 text-center'>
          <p className='text-sm text-fg-tertiary'>
            {canCreate
              ? `No ${group.label.toLowerCase()} pipelines yet. Create one with the button above, or ask the agent to set one up for you.`
              : `No ${group.label.toLowerCase()} pipelines yet. Ask the agent to set one up for you.`}
          </p>
        </div>
      ) : (
        <div className='rounded-lg border border-border-secondary overflow-hidden'>
          {/* ScrollArea provides horizontal overflow + edge-fade hints; the base
              Table is only `w-full` and would otherwise clip its last column in a
              narrow panel. */}
          <TableCard.ScrollArea>
            <Table aria-label={`${group.label} pipelines`} size='sm'>
              <Table.Header>
                <Table.Head label='Name' isRowHeader />
                <Table.Head label='Stages' />
                <Table.Head label='Actions' align='right' />
              </Table.Header>
              <Table.Body>
                {group.pipelines.map(pipeline => (
                  <PipelineRow
                    key={
                      pipeline.entity_id ??
                      `${pipeline.entity_type_name}:${pipeline.pipeline_name}`
                    }
                    pipeline={pipeline}
                    orgId={orgId}
                    onEditStages={onEditStages}
                    onSetDefault={onSetDefault}
                    onDelete={onDelete}
                  />
                ))}
              </Table.Body>
            </Table>
          </TableCard.ScrollArea>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// PipelinesContent
// ============================================================================

interface CreatePipelineState {
  baseEntityTypeName: string;
  label: string;
  existingNames: string[];
}

function PipelinesContent() {
  const { currentOrg } = useOrg();
  const orgId = currentOrg?.id;
  const queryClient = useQueryClient();
  const { user } = useAuthInfo();
  const { hasPermission } = usePermissions();

  const { pipelines: allPipelines = [], isLoading: isPipelinesLoading } =
    usePipelines(orgId);
  const pipelines = allPipelines;

  // Resolve org-configured singular labels (e.g. "Deal", "Note", custom
  // entities) so group headings match the rest of the product. Falls back to a
  // formatted type name when an object type isn't in the schema response.
  const { data: objectTypesData, isLoading: isObjectTypesLoading } =
    useObjectTypes(orgId);
  // Sections are derived from both queries, so keep the skeleton up until both
  // have resolved — otherwise a slower object-types fetch renders a blank panel
  // with neither a loading indicator nor an empty state.
  const isLoading = isPipelinesLoading || isObjectTypesLoading;
  const labelByType = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of objectTypesData?.allObjectTypes ?? []) {
      map.set(t.entity_type_name, t.label);
    }
    return map;
  }, [objectTypesData]);

  // Every schema object type whose name ends in `_pipeline` is a pipeline-capable
  // type; the base entity is the name minus the suffix (same derivation
  // getBaseEntityType uses in reverse).
  const pipelineCapableTypes = useMemo(() => {
    const out: {
      pipelineEntityTypeName: string;
      baseEntityTypeName: string;
      userCreatable: boolean;
    }[] = [];
    for (const t of objectTypesData?.allObjectTypes ?? []) {
      if (!t.entity_type_name.endsWith('_pipeline')) {
        continue;
      }
      out.push({
        pipelineEntityTypeName: t.entity_type_name,
        baseEntityTypeName: t.entity_type_name.replace(/_pipeline$/, ''),
        userCreatable: t.user_creatable !== false,
      });
    }
    return out;
  }, [objectTypesData]);

  // Union of schema-capable pipeline types with types that have existing
  // pipelines, deal pipelines first then alphabetical by label.
  const groups = useMemo<PipelineGroup[]>(() => {
    const byType = new Map<string, Pipeline[]>();
    for (const p of pipelines) {
      const existing = byType.get(p.entity_type_name);
      if (existing) {
        existing.push(p);
      } else {
        byType.set(p.entity_type_name, [p]);
      }
    }

    // Ordered union of pipeline entity types. Schema-capable + user-creatable
    // types come first (so an empty type still gets a section with a create
    // path); then any type that actually has pipelines is guaranteed a slot.
    const typeMeta = new Map<
      string,
      { base: string; userCreatable: boolean }
    >();
    const schemaByType = new Map(
      pipelineCapableTypes.map(t => [t.pipelineEntityTypeName, t])
    );
    for (const t of pipelineCapableTypes) {
      if (!t.userCreatable) {
        continue; // don't offer creates the backend rejects
      }
      if (t.pipelineEntityTypeName === NOTE_PIPELINE_ENTITY_TYPE_NAME) {
        continue; // notes are retired — never mint an empty note-pipeline group
      }
      typeMeta.set(t.pipelineEntityTypeName, {
        base: t.baseEntityTypeName,
        userCreatable: true,
      });
    }
    byType.forEach((list, typeName) => {
      if (
        typeName === NOTE_PIPELINE_ENTITY_TYPE_NAME &&
        !hasPermission(NOTE_BASE_ENTITY_TYPE_NAME)
      ) {
        // Existing note pipelines only surface while the viewer still has
        // Note read access; otherwise treat the type as if it had no rows.
        return;
      }
      if (!typeMeta.has(typeName)) {
        // Populated type absent from the creatable set — either the schema
        // hasn't loaded yet, or the schema marks it `user_creatable: false`.
        // Existing pipelines always get a section; the create button follows
        // the schema when we have it, and defaults to true only when we don't
        // (backend still enforces).
        const schemaType = schemaByType.get(typeName);
        typeMeta.set(typeName, {
          base: schemaType?.baseEntityTypeName ?? getBaseEntityType(list[0]),
          userCreatable: schemaType?.userCreatable ?? true,
        });
      }
    });

    const result: PipelineGroup[] = [];
    typeMeta.forEach(({ base, userCreatable }, pipelineEntityTypeName) => {
      result.push({
        pipelineEntityTypeName,
        baseEntityTypeName: base,
        label: labelByType.get(base) ?? getEntityTypeLabel(base),
        userCreatable,
        pipelines: byType.get(pipelineEntityTypeName) ?? [],
      });
    });

    result.sort((a, b) => {
      if (a.pipelineEntityTypeName === 'ahoy_deal_pipeline') {
        return -1;
      }
      if (b.pipelineEntityTypeName === 'ahoy_deal_pipeline') {
        return 1;
      }
      return a.label.localeCompare(b.label);
    });
    return result;
  }, [pipelines, pipelineCapableTypes, labelByType, hasPermission]);

  const [createState, setCreateState] = useState<CreatePipelineState | null>(
    null
  );
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingStagesPipeline, setEditingStagesPipeline] =
    useState<Pipeline | null>(null);
  const [deletingPipeline, setDeletingPipeline] = useState<Pipeline | null>(
    null
  );
  const [isDeleting, setIsDeleting] = useState(false);

  const handleOpenCreate = useCallback((group: PipelineGroup) => {
    setEditingStagesPipeline(null);
    setDeletingPipeline(null);
    setCreateState({
      baseEntityTypeName: group.baseEntityTypeName,
      label: group.label,
      existingNames: group.pipelines.map(p => p.pipeline_name),
    });
    setIsCreateOpen(true);
  }, []);

  const handleCreateOpenChange = useCallback(
    (open: boolean) => {
      setIsCreateOpen(open);
      if (!open && orgId) {
        queryClient.invalidateQueries({
          queryKey: ['pipelines', orgId],
          exact: false,
        });
      }
    },
    [orgId, queryClient]
  );

  const handleEditStages = useCallback((_p: Pipeline) => {
    setIsCreateOpen(false);
    setDeletingPipeline(null);
    setEditingStagesPipeline(_p);
  }, []);

  // Set a pipeline as the default for its record type. This is just two
  // property writes (clear the old default, set the new one) submitted as a
  // single batch through the standard crm_events submit-events API — no bespoke
  // endpoint. See docs/claude/FRONTEND_PATTERNS.md § "Mutate CRM data through
  // the events API".
  const handleSetDefault = useCallback(
    async (pipeline: Pipeline) => {
      if (!pipeline.entity_id || !orgId || !user) {
        return;
      }
      try {
        // Fetch a fresh object state and a fresh pipeline list in parallel so
        // we don't act on stale React Query cache when identifying the current
        // default (staleTime is 5 min, so the cache can easily be out-of-date).
        const [newObj] = await Promise.all([
          createCRMObjectClient(orgId, pipeline.entity_type_name).getByEntityId(
            pipeline.entity_id
          ),
          queryClient.refetchQueries({
            queryKey: ['pipelines', orgId],
            exact: false,
          }),
        ]);
        const freshPipelines =
          queryClient.getQueryData<Pipeline[]>([
            'pipelines',
            orgId,
            undefined,
          ]) ?? [];
        const newBuilder = createEventBuilder({
          sourceType: 'ahoy_user',
          sourceId: String(user.id),
          startSeq: newObj.last_event_seq,
        });
        newBuilder.propertiesUpdated(
          pipeline.entity_id,
          pipeline.entity_type_name,
          { ahoy_is_default: 'true' }
        );
        const events = [...newBuilder.build()];

        // Clear the previous default of the same record type, if any.
        const currentDefault = freshPipelines.find(
          p =>
            p.is_default &&
            p.entity_type_name === pipeline.entity_type_name &&
            p.entity_id &&
            p.entity_id !== pipeline.entity_id
        );
        if (currentDefault?.entity_id) {
          const oldSeq = await fetchFreshSeq(
            orgId,
            currentDefault.entity_type_name,
            currentDefault.entity_id
          );
          const oldBuilder = createEventBuilder({
            sourceType: 'ahoy_user',
            sourceId: String(user.id),
            startSeq: oldSeq,
          });
          oldBuilder.propertiesUpdated(
            currentDefault.entity_id,
            currentDefault.entity_type_name,
            { ahoy_is_default: 'false' }
          );
          events.push(...oldBuilder.build());
        }

        await createEventsClient(orgId).submitEvents(events);

        await queryClient.invalidateQueries({
          queryKey: ['pipelines', orgId],
          exact: false,
        });

        toast.success(`"${pipeline.label}" is now the default`);
      } catch (err) {
        toast.error(getUserFriendlyEventError(err));
        logger.error(
          'Failed to set default pipeline',
          err instanceof Error ? err : new Error(String(err)),
          { org_id: orgId, entity_id: pipeline.entity_id }
        );
      }
    },
    [orgId, user, queryClient]
  );

  const handleDelete = useCallback((_p: Pipeline) => {
    setIsCreateOpen(false);
    setEditingStagesPipeline(null);
    setDeletingPipeline(_p);
  }, []);

  const handleClearDeleting = useCallback(() => {
    setDeletingPipeline(null);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!deletingPipeline?.entity_id || !orgId || !user) {
      return;
    }

    setIsDeleting(true);

    try {
      const freshSeq = await fetchFreshSeq(
        orgId,
        deletingPipeline.entity_type_name,
        deletingPipeline.entity_id
      );

      const builder = createEventBuilder({
        sourceType: 'ahoy_user',
        sourceId: String(user.id),
        startSeq: freshSeq,
      });

      builder.objectDeleted(
        deletingPipeline.entity_id,
        deletingPipeline.entity_type_name
      );

      const eventsClient = createEventsClient(orgId);
      await eventsClient.submitEvents(builder.build());

      await queryClient.invalidateQueries({
        queryKey: ['pipelines', orgId],
        exact: false,
      });
      await queryClient.invalidateQueries({
        queryKey: ['crm', orgId, 'objects', deletingPipeline.entity_type_name],
        exact: false,
      });

      toast.success('Pipeline deleted');
      setDeletingPipeline(null);
    } catch (err) {
      toast.error(getUserFriendlyEventError(err));
      logger.error(
        'Failed to delete pipeline',
        err instanceof Error ? err : new Error(String(err)),
        { org_id: orgId, entity_id: deletingPipeline.entity_id }
      );
    } finally {
      setIsDeleting(false);
    }
  }, [deletingPipeline, orgId, user, queryClient]);

  const handleCloseStages = useCallback(() => {
    setEditingStagesPipeline(null);
  }, []);

  const handleStagesSuccess = useCallback(() => {
    if (!orgId) {
      return;
    }
    queryClient.invalidateQueries({
      queryKey: ['pipelines', orgId],
      exact: false,
    });
  }, [orgId, queryClient]);

  const handlePipelineDetailsSave = useCallback(
    async (updates: { name?: string; description?: string }) => {
      if (!user || !editingStagesPipeline?.entity_id || !orgId) {
        throw new Error('Missing context for pipeline update');
      }
      if (updates.name === undefined && updates.description === undefined) {
        return;
      }
      const entityId = editingStagesPipeline.entity_id;
      try {
        const crmObject = await createCRMObjectClient(
          orgId,
          editingStagesPipeline.entity_type_name
        ).getByEntityId(entityId);

        const builder = createEventBuilder({
          sourceType: 'ahoy_user',
          sourceId: String(user.id),
          startSeq: crmObject.last_event_seq,
        });

        const properties: Record<string, string> = {};
        if (updates.name !== undefined) {
          properties.ahoy_name = updates.name;
        }
        if (updates.description !== undefined) {
          properties.ahoy_description = updates.description;
        }

        builder.propertiesUpdated(
          entityId,
          editingStagesPipeline.entity_type_name,
          properties
        );

        const eventsClient = createEventsClient(orgId);
        await eventsClient.submitEvents(builder.build());

        await queryClient.invalidateQueries({
          queryKey: ['pipelines', orgId],
          exact: false,
        });
        await queryClient.invalidateQueries({
          queryKey: [
            'crm',
            orgId,
            'objects',
            editingStagesPipeline.entity_type_name,
          ],
          exact: false,
        });
      } catch (error) {
        logger.error(
          'Failed to update pipeline details',
          error instanceof Error ? error : new Error(String(error)),
          { org_id: orgId, entity_id: entityId }
        );
        throw new Error(getUserFriendlyEventError(error));
      }
    },
    [user, editingStagesPipeline, orgId, queryClient]
  );

  const stagesConfig = useMemo(() => {
    if (!editingStagesPipeline) {
      return null;
    }
    const baseType =
      editingStagesPipeline.base_entity_type_name ||
      editingStagesPipeline.entity_type_name.replace('_pipeline', '');
    const stageType = `${baseType}_pipeline_stage`;
    return {
      mode: 'pipeline',
      stageEntityTypeName: stageType,
      pipelineName: editingStagesPipeline.pipeline_name,
      associationTypeName: `${baseType}_to_${stageType}`,
      targetObjectTypeName: baseType,
      showProbability:
        editingStagesPipeline.entity_type_name === 'ahoy_deal_pipeline',
      title: `Edit pipeline – ${editingStagesPipeline.label}`,
      description: 'Add, remove, or reorder pipeline stages',
    } satisfies StagesSlideoutConfig;
  }, [editingStagesPipeline]);

  const editingEntityLabel = useMemo(() => {
    if (!editingStagesPipeline) {
      return undefined;
    }
    const baseType = getBaseEntityType(editingStagesPipeline);
    return labelByType.get(baseType) ?? getEntityTypeLabel(baseType);
  }, [editingStagesPipeline, labelByType]);

  return (
    <>
      <div className='space-y-8'>
        {isLoading && (
          <div
            role='status'
            aria-live='polite'
            aria-label='Loading pipelines'
            className='space-y-3'
          >
            {[1, 2, 3].map(i => (
              <div
                key={i}
                className='h-12 bg-bg-tertiary rounded animate-pulse'
              />
            ))}
          </div>
        )}

        {!isLoading &&
          orgId &&
          groups.map(group => (
            <PipelineGroupSection
              key={group.pipelineEntityTypeName}
              group={group}
              orgId={orgId}
              onCreate={handleOpenCreate}
              onEditStages={handleEditStages}
              onSetDefault={handleSetDefault}
              onDelete={handleDelete}
            />
          ))}
      </div>

      {orgId && createState && (
        <CreatePipelineSlideout
          // Remount when switching entity types so the slideout reseeds its
          // type-correct default stages (deal vs. generic) on open.
          key={createState.baseEntityTypeName}
          isOpen={isCreateOpen}
          onOpenChange={handleCreateOpenChange}
          orgId={orgId}
          // Deal stays implicit (undefined) to preserve the original default
          // create path; other types pass their base entity type explicitly.
          entityType={
            createState.baseEntityTypeName === 'ahoy_deal'
              ? undefined
              : createState.baseEntityTypeName
          }
          entityLabel={createState.label}
          existingPipelineNames={createState.existingNames}
        />
      )}

      {editingStagesPipeline &&
        editingStagesPipeline.entity_id &&
        stagesConfig && (
          <StagesSlideout
            config={stagesConfig}
            isOpen={!!editingStagesPipeline}
            onClose={handleCloseStages}
            pipelineId={editingStagesPipeline.entity_id}
            onSuccess={handleStagesSuccess}
            initialName={editingStagesPipeline.label}
            initialDescription={editingStagesPipeline.description ?? ''}
            onPipelineDetailsSave={handlePipelineDetailsSave}
            entityLabel={editingEntityLabel}
          />
        )}

      {deletingPipeline && (
        <ConfirmationDialog
          isOpen={!!deletingPipeline}
          title='Delete pipeline?'
          description={`Are you sure you want to delete "${deletingPipeline.label}"? This will remove all ${deletingPipeline.stages.length} stages. Deals must be moved to another pipeline first.`}
          variant='destructive'
          confirmLabel='Delete'
          isLoading={isDeleting}
          onConfirm={handleConfirmDelete}
          onCancel={handleClearDeleting}
        />
      )}
    </>
  );
}

// ============================================================================
// ContactLifecycleContent
// ============================================================================

function ContactLifecycleContent() {
  const { hasPermission } = usePermissions();
  const queryClient = useQueryClient();
  const { currentOrg } = useOrg();
  const orgId = currentOrg?.id;
  const [isLifecycleOpen, setIsLifecycleOpen] = useState(false);

  const canEditLifecycle = hasPermission(
    'ahoy_contact_lifecycle_stage',
    'ahoy_property_updated'
  );

  const handleOpen = useCallback(() => setIsLifecycleOpen(true), []);
  const handleClose = useCallback(() => setIsLifecycleOpen(false), []);
  const handleSuccess = useCallback(() => {
    if (!orgId) {
      return;
    }
    // Stage-only updates land as CRM events; refresh anything keyed off
    // ``ahoy_contact_lifecycle_stage`` so list views and pickers re-pull.
    queryClient.invalidateQueries({
      queryKey: ['stages', orgId],
      exact: false,
    });
    queryClient.invalidateQueries({
      queryKey: ['crm', orgId, 'objects', 'ahoy_contact_lifecycle_stage'],
      exact: false,
    });
  }, [orgId, queryClient]);

  return (
    <>
      <div className='rounded-lg border border-border-secondary p-4 flex items-center justify-between gap-3'>
        <div className='flex items-center gap-3'>
          <div className='flex h-8 w-8 items-center justify-center rounded-lg bg-bg-secondary'>
            <UserUp01 className='h-4 w-4 text-fg-tertiary' />
          </div>
          <div>
            <p className='text-sm font-medium text-fg-primary'>
              Contact lifecycle
            </p>
            <p className='text-sm text-fg-tertiary mt-0.5'>
              The stages a contact moves through, from prospect to customer.
            </p>
          </div>
        </div>
        <Tooltip
          title={
            canEditLifecycle
              ? 'Edit lifecycle stages'
              : 'You do not have permission to edit lifecycle stages'
          }
        >
          <Button
            size='sm'
            color='secondary'
            iconLeading={Settings01}
            onClick={handleOpen}
            isDisabled={!canEditLifecycle}
          >
            Edit stages
          </Button>
        </Tooltip>
      </div>

      {orgId && (
        <StagesSlideout
          config={lifecycleStagesConfig}
          isOpen={isLifecycleOpen}
          onClose={handleClose}
          onSuccess={handleSuccess}
          entityLabel='Contact'
        />
      )}
    </>
  );
}

// ============================================================================
// PipelinesSection
// ============================================================================

export function PipelinesSection() {
  useViewHealthMarker('settings-section-ready');

  return (
    <div className='space-y-10'>
      <section className='space-y-4'>
        <div>
          <h2 className='text-lg font-semibold text-fg-primary'>Pipelines</h2>
          <p className='text-sm text-fg-tertiary mt-0.5'>
            Manage your sales pipelines and stages.
          </p>
        </div>
        <PipelinesContent />
      </section>

      <section className='space-y-4'>
        <div>
          <h2 className='text-lg font-semibold text-fg-primary'>
            Contact lifecycle
          </h2>
          <p className='text-sm text-fg-tertiary mt-0.5'>
            Customize the stages contacts move through across their lifecycle.
          </p>
        </div>
        <ContactLifecycleContent />
      </section>
    </div>
  );
}
